<script setup lang="ts">
import { RouterView, useRoute } from 'vue-router'
import NavigationHeader from './components/NavigationHeader.vue'
import { useAuthStore } from './stores/auth'
import { useSystemStore } from './stores/system'
import { storeToRefs } from 'pinia'
import { onMounted, computed, ref } from 'vue'
import SupervisorApprovalModal from './components/SupervisorApprovalModal.vue'

const authStore = useAuthStore()
const systemStore = useSystemStore()
const route = useRoute()
const { isAuthenticated, loading } = storeToRefs(authStore)

// Track if we're in the initial loading phase
const initialLoading = ref(true)

// Hide NavigationHeader on dashboard routes, admin routes, and new design system pages
const hiddenRoutes = ['/', '/terms', '/privacy', '/about', '/pricing', '/features', '/platform']
const showNavigationHeader = computed(() =>
  !route.path.startsWith('/dashboard') &&
  !route.path.startsWith('/admin') &&
  !hiddenRoutes.includes(route.path)
)

// Determine if we should show loading state
const showLoading = computed(() => {
  // Show loading if auth is still loading, OR if we're in the initial 300ms grace period
  return loading.value || initialLoading.value
})

// Set API URL for legacy dashboard
onMounted(() => {
  systemStore.initTheme() // Initialize theme on app mount
  systemStore.loadSystemData() // Load models and presets early

  const apiUrl = import.meta.env.VITE_API_URL || window.location.origin
  // Set meta tag for legacy dashboard
  const metaTag = document.querySelector('meta[name="api-url"]')
  if (metaTag) {
    metaTag.setAttribute('content', apiUrl)
  } else {
    const newMetaTag = document.createElement('meta')
    newMetaTag.name = 'api-url'
    newMetaTag.content = apiUrl
    document.head.appendChild(newMetaTag)
  }

  // Also set window object for legacy dashboard
  ;(window as any).ZULU_API_URL = apiUrl

  // Clear initial loading after a short delay to allow for auth state resolution
  setTimeout(() => {
    initialLoading.value = false
  }, 300)
})
</script>

<template>
  <div class="app-container">
    <SupervisorApprovalModal />
    <!-- Loading overlay for initial auth check -->
    <div 
      v-if="showLoading" 
      class="loading-overlay"
    >
      <div class="loading-content">
        <div class="spinner"></div>
        <p class="loading-text">Loading your workspace...</p>
      </div>
    </div>

    <!-- Main app content -->
    <div v-else class="app-content">
      <NavigationHeader v-if="showNavigationHeader" />
      <router-view v-slot="{ Component }">
        <keep-alive include="DashboardView">
          <component :is="Component" />
        </keep-alive>
      </router-view>
    </div>
  </div>
</template>

<style scoped>
.app-container {
  width: 100%;
  min-height: 100%;
}

.app-content {
  width: 100%;
  min-height: 100%;
}

.router-view {
  width: 100%;
  min-height: calc(100vh - 64px); /* 64px = 4rem for the header height */
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.3s ease-in-out;
}

.loading-content {
  text-align: center;
  color: white;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

.loading-text {
  font-size: 1.1rem;
  font-weight: 500;
  opacity: 0.9;
  margin: 0;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
